import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  aiProviders,
  reports,
  schedule as scheduleApi,
  websites as websitesApi,
} from "../../api/endpoints";
import DashboardFilters, {
  defaultDashboardFilters,
} from "../../components/DashboardFilters.jsx";
import InfoTooltip from "../../components/InfoTooltip.jsx";
import ModelBadge, { modelColor, modelDisplayName } from "../../components/ModelBadge.jsx";
import SiteFavicon from "../../components/SiteFavicon.jsx";
import { useSelectedWebsite } from "./Layout.jsx";

const SOV_PALETTE = ["#2ea7af", "#a3dde1", "#cfeff1", "#62c4ca", "#e8f6f7"];
const FALLBACK_MODEL_KEYS = ["openai", "anthropic", "gemini"];

export default function Home() {
  const { t } = useTranslation();
  const { websiteId } = useSelectedWebsite();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [website, setWebsite] = useState(null);
  // Which per-model cards/trends to render — fetched from
  // /api/ai/providers/ so a newly-configured provider (e.g. Google AI
  // Overview) appears here without a code change.
  const [modelKeys, setModelKeys] = useState(FALLBACK_MODEL_KEYS);
  // Phase 2: filter state lives at the dashboard root so every chart
  // reads from the same window. Defaults: last 28 days, all LLMs.
  const [filters, setFilters] = useState(() => defaultDashboardFilters(28));

  useEffect(() => {
    aiProviders
      .list()
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        const configured = list.filter((p) => p.configured).map((p) => p.value);
        if (configured.length) setModelKeys(configured);
      })
      .catch(() => {});
  }, []);

  const reloadDashboard = () => {
    if (!websiteId) return Promise.resolve();
    setLoading(true);
    return reports
      .dashboard(websiteId, filters)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reloadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId, filters.start, filters.end, filters.ai_model]);

  useEffect(() => {
    if (!websiteId) return;
    // Phase 1: website meta (favicon, niche) for the header. Best-effort.
    websitesApi
      .list()
      .then((r) => setWebsite(r.data.find((w) => w.id === websiteId) || null))
      .catch(() => setWebsite(null));
  }, [websiteId]);

  if (!websiteId) return <EmptyState />;

  return (
    <div className="space-y-6 sm:space-y-8">
      <header className="flex items-start sm:items-center gap-3">
        {website && (
          <SiteFavicon
            url={website.favicon_url}
            domain={website.domain}
            label={website.label}
            size={28}
          />
        )}
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight truncate">
            {website?.label || t("home.title")}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            {website?.niche
              ? `${website.niche} · ${t("home.subtitle")}`
              : t("home.subtitle")}
          </p>
        </div>
        {website && typeof website.ahrefs_domain_rating === "number" && (
          <div
            className="ml-auto flex items-center gap-3 rounded-2xl bg-white border border-slate-200/70 shadow-sm px-4 py-2.5"
            title={t("home.daTooltip")}
          >
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                {t("home.daLabel")}
              </div>
              <div className="mono text-lg font-bold text-slate-900">
                {website.ahrefs_domain_rating}
              </div>
            </div>
            {typeof website.ahrefs_organic_traffic === "number" && (
              <div className="border-l border-slate-200 pl-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  {t("home.daTrafficLabel")}
                </div>
                <div className="mono text-lg font-bold text-slate-900">
                  {website.ahrefs_organic_traffic.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Filters + NextRun on mobile stack one on top of the other —
          the filter dropdowns alone need the full row. */}
      <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-stretch gap-3">
        <div className="flex-1 lg:min-w-[300px]">
          <DashboardFilters value={filters} onChange={setFilters} disabled={loading} />
        </div>
        <NextRunBadge
          schedule={data?.schedule}
          websiteId={websiteId}
          onRefresh={reloadDashboard}
          t={t}
        />
      </div>

      {loading && !data ? (
        <LoadingState />
      ) : !data ? (
        <div className="text-slate-500">{t("home.notEnoughData")}</div>
      ) : (
        <DashboardBody data={data} loading={loading} t={t} modelKeys={modelKeys} />
      )}
    </div>
  );
}

function NextRunBadge({ schedule, websiteId, onRefresh, t }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!schedule || !schedule.next_run_at) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200/70 shadow-sm px-4 py-3 flex items-center gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5">
            {t("home.nextRunLabel")}
            <InfoTooltip text={t("home.nextRunTooltip")} />
          </div>
          <div className="text-sm font-medium text-slate-500 mt-1">
            {t("home.nextRunNone")}
          </div>
        </div>
      </div>
    );
  }
  const next = new Date(schedule.next_run_at);
  const isOverdue = next.getTime() <= Date.now();
  const lowTokens = Boolean(schedule.insufficient_tokens);
  // "Run now" appears when the run is overdue, the schedule is
  // active, AND tokens are sufficient — i.e. exactly when the
  // dispatcher *could* fire on its next hourly tick. The button
  // lets the user skip that wait. Stuck-schedule recovery: a top-up
  // also auto-advances ``next_run_at`` server-side (see
  // ``_wake_overdue_schedules``), so the dispatcher picks it up
  // even without the user clicking.
  const canRunNow =
    schedule.is_active && isOverdue && !lowTokens && Boolean(websiteId);

  async function onRunNow() {
    if (!websiteId || busy) return;
    setBusy(true);
    setError(null);
    try {
      await scheduleApi.runNow(websiteId);
      if (onRefresh) await onRefresh();
    } catch (err) {
      const data = err?.response?.data;
      setError(data?.detail || t("home.nextRunRunFailed"));
    } finally {
      setBusy(false);
    }
  }

  const cardClass = lowTokens
    ? "rounded-2xl bg-amber-50/80 border border-amber-300 shadow-sm px-4 py-3"
    : "rounded-2xl bg-white border border-slate-200/70 shadow-sm px-4 py-3";
  return (
    <div className={cardClass}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5">
        {t("home.nextRunLabel")}
        <InfoTooltip text={t("home.nextRunTooltip")} />
      </div>
      <div
        className={`text-sm font-medium mt-1 ${
          lowTokens
            ? "text-amber-900"
            : isOverdue
              ? "text-amber-700"
              : "text-slate-900"
        }`}
      >
        {isOverdue ? t("home.nextRunDue") : next.toLocaleString()}
      </div>
      {!schedule.is_active && (
        <div className="text-[11px] text-slate-500 mt-0.5">
          {t("home.nextRunPaused")}
        </div>
      )}
      {lowTokens && schedule.is_active && (
        <div className="text-[11px] text-amber-800 mt-1 leading-snug">
          {t("home.nextRunInsufficientTokens", {
            needed: schedule.estimated_cost_per_run || 0,
            have: schedule.token_balance || 0,
          })}{" "}
          <Link
            to="/dashboard/billing"
            className="underline hover:text-amber-900"
          >
            {t("home.nextRunTopUp")}
          </Link>
        </div>
      )}
      {canRunNow && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onRunNow}
            disabled={busy}
            className="text-[11px] rounded-md px-2.5 py-1 bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? t("home.nextRunRunning") : t("home.nextRunRunNow")}
          </button>
          <span className="text-[11px] text-slate-500">
            {t("home.nextRunRunNowHint")}
          </span>
        </div>
      )}
      {error && (
        <div className="text-[11px] text-red-700 mt-1">{error}</div>
      )}
    </div>
  );
}

function DashboardBody({ data, loading, t, modelKeys }) {
  return (
    <div className={`space-y-6 sm:space-y-8 transition-opacity ${loading ? "opacity-60" : ""}`}>

      {/* KPI strip — 2 columns on mobile so each card stays
          readable; 4 columns on desktop. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Kpi
          label={t("home.kpiCoverage")}
          value={`${data.brand_coverage_rate}%`}
          delta={data.coverage_change_pct}
          deltaSuffix="pp"
          accent="#2ea7af"
          tooltip={t("home.tooltipCoverage")}
        />
        <Kpi
          label={t("home.kpiVisibility")}
          value={(data.average_visibility_score || 0).toFixed(2)}
          accent="#1f8990"
          tooltip={t("home.tooltipVisibility")}
        />
        <Kpi
          label={t("home.kpiCitations")}
          value={data.total_citations}
          accent="#62c4ca"
          tooltip={t("home.tooltipCitations")}
        />
        <Kpi
          label={t("home.kpiScans")}
          value={data.total_scans}
          accent="#a3dde1"
          tooltip={t("home.tooltipScans")}
        />
      </div>

      {/* Per-LLM breakdown */}
      <Section
        title={t("home.perModelTitle")}
        subtitle={t("home.perModelSubtitle")}
        tooltip={t("home.tooltipPerModel")}
      >
        <div className="grid sm:grid-cols-3 gap-4">
          {modelKeys.map((key) => (
            <PerModelCard
              key={key}
              modelKey={key}
              metrics={data.per_model_metrics?.[key]}
              t={t}
            />
          ))}
        </div>
      </Section>

      {/* Coverage trend (multi-line) + Share of Voice */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Section
          className="lg:col-span-2"
          title={t("home.coverageTrend")}
          tooltip={t("home.tooltipCoverageTrend")}
        >
          <PerModelTrendChart data={data.coverage_trend_by_model} t={t} modelKeys={modelKeys} />
        </Section>

        <Section
          title={t("home.shareOfVoice")}
          tooltip={t("home.tooltipShareOfVoice")}
        >
          <ShareOfVoice data={data.share_of_voice} />
        </Section>
      </div>

      {/* Scan activity — how many scans ran per day in the selected
          window. Built from the same coverage_trend the trend chart
          above already uses, just plotted as counts instead of rates. */}
      <Section
        title={t("home.scanActivityTitle")}
        subtitle={t("home.scanActivitySubtitle")}
        tooltip={t("home.tooltipScanActivity")}
      >
        <ScanActivityChart data={data.coverage_trend} t={t} />
      </Section>

      {/* Visibility-score multi-line trend (Phase 3) */}
      <Section
        title={t("home.visibilityTrendTitle")}
        subtitle={t("home.visibilityTrendSubtitle")}
        tooltip={t("home.tooltipVisibilityTrend")}
      >
        <PerModelVisibilityTrend data={data.visibility_trend_by_model} t={t} modelKeys={modelKeys} />
      </Section>

      {/* Sentiment + aggregate Position rank */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title={t("home.sentiment")} tooltip={t("home.tooltipSentiment")}>
          <SentimentChart breakdown={data.sentiment_breakdown} t={t} />
        </Section>

        <Section
          title={t("home.positionDistribution")}
          subtitle={t("home.positionSubtitle")}
          tooltip={t("home.tooltipPosition")}
        >
          <PositionDistChart dist={data.position_distribution} t={t} />
        </Section>
      </div>

      {/* Per-LLM position breakdown */}
      <Section
        title={t("home.positionByLlmTitle")}
        subtitle={t("home.positionByLlmSubtitle")}
        tooltip={t("home.tooltipPositionByLlm")}
      >
        <PositionByModel
          distByModel={data.position_distribution_by_model}
          t={t}
          modelKeys={modelKeys}
        />
      </Section>

      {/* Top prompts */}
      <Section
        title={t("home.topPromptsTitle")}
        subtitle={t("home.topPromptsSubtitle")}
        tooltip={t("home.tooltipTopPrompts")}
      >
        <TopPromptsTable prompts={data.top_prompts || []} t={t} />
      </Section>
    </div>
  );
}

/* ─── Reusable building blocks ─────────────────────────────────── */

function Section({ title, subtitle, className = "", children, tooltip }) {
  return (
    <section className={`rounded-2xl bg-white border border-slate-200/70 shadow-sm p-6 ${className}`}>
      <div className="flex justify-between items-baseline mb-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            {title}
            {tooltip && <InfoTooltip text={tooltip} />}
          </h3>
          {subtitle && (
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

function Kpi({ label, value, delta, deltaSuffix, accent, tooltip }) {
  return (
    <div
      className="rounded-2xl bg-white border border-slate-200/70 shadow-sm p-5 relative overflow-hidden hover:shadow-md transition-shadow"
    >
      <div
        className="absolute top-0 left-0 h-1 w-full"
        style={{ background: accent }}
      />
      <div className="text-xs text-slate-500 uppercase tracking-wider font-medium inline-flex items-center gap-1.5">
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
      <div className="mono text-3xl font-bold text-slate-900 mt-2">{value}</div>
      {typeof delta === "number" && (
        <DeltaPill value={delta} suffix={deltaSuffix} />
      )}
    </div>
  );
}

function DeltaPill({ value, suffix = "" }) {
  const positive = value > 0;
  const negative = value < 0;
  const klass = positive
    ? "text-emerald-700 bg-emerald-50 ring-emerald-200"
    : negative
      ? "text-red-700 bg-red-50 ring-red-200"
      : "text-slate-600 bg-slate-50 ring-slate-200";
  const arrow = positive ? "↑" : negative ? "↓" : "→";
  return (
    <div
      className={`mt-2 inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 ring-1 ${klass}`}
    >
      <span>{arrow}</span>
      <span className="mono">
        {value > 0 ? "+" : ""}
        {value}
        {suffix}
      </span>
    </div>
  );
}

function PerModelCard({ modelKey, metrics, t }) {
  const m = metrics || { scans: 0, coverage_rate: 0, avg_visibility_score: 0, brand_citations: 0 };
  const color = modelColor(modelKey);
  const empty = m.scans === 0;
  return (
    <div className="rounded-xl border border-slate-200/70 p-5 hover:shadow-md transition-shadow relative overflow-hidden">
      <div
        className="absolute top-0 left-0 h-1 w-full"
        style={{ background: color }}
      />
      <div className="flex items-center gap-2 mb-4">
        <ModelBadge modelKey={modelKey} size="sm" />
      </div>

      {/* Big number = coverage rate, with a gauge underneath */}
      <div className="mb-3">
        <div className="text-xs text-slate-500 uppercase tracking-wider">
          {t("home.kpiCoverage")}
        </div>
        <div className="mono text-3xl font-bold mt-1" style={{ color }}>
          {empty ? "—" : `${m.coverage_rate}%`}
        </div>
        <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: empty ? "0%" : `${Math.min(100, m.coverage_rate)}%`,
              background: color,
            }}
          />
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100">
        <Stat label={t("home.kpiVisibility")} value={empty ? "—" : m.avg_visibility_score.toFixed(2)} />
        <Stat label={t("home.kpiCitations")} value={empty ? "—" : m.brand_citations} />
        <Stat label={t("home.kpiScans")} value={empty ? "—" : m.scans} />
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</div>
      <div className="mono text-sm font-semibold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function PerModelTrendChart({ data, t, modelKeys }) {
  // Recharts needs each series merged onto a single date timeline.
  // Build a flat list: [{date, openai, anthropic, gemini, ...}, ...].
  const dateSet = new Set();
  for (const key of modelKeys) {
    for (const p of data?.[key] || []) dateSet.add(p.date);
  }
  if (dateSet.size === 0) {
    return <ChartEmpty />;
  }
  const dates = Array.from(dateSet).sort();
  const merged = dates.map((date) => {
    const row = { date };
    for (const k of modelKeys) {
      const point = (data[k] || []).find((p) => p.date === date);
      row[k] = point ? point.coverage_rate : null;
    }
    return row;
  });
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={merged} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
        <CartesianGrid stroke="#eef2f7" vertical={false} />
        <XAxis
          dataKey="date"
          stroke="#94a3b8"
          fontSize={11}
          tickLine={false}
          axisLine={{ stroke: "#e2e8f0" }}
        />
        <YAxis
          stroke="#94a3b8"
          fontSize={11}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v) => `${v}%`}
        />
        <Tooltip
          formatter={(v, n) => [v === null ? "—" : `${v}%`, modelDisplayName(n)]}
          contentStyle={{
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            fontSize: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        />
        <Legend
          formatter={(v) => modelDisplayName(v)}
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontSize: 12 }}
        />
        {modelKeys.map((k) => (
          <Line
            key={k}
            type="monotone"
            dataKey={k}
            stroke={modelColor(k)}
            strokeWidth={2.5}
            dot={{ r: 3, fill: modelColor(k), strokeWidth: 0 }}
            activeDot={{ r: 5 }}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function PerModelVisibilityTrend({ data, t, modelKeys }) {
  // Same flattening trick as PerModelTrendChart but plotting
  // ``avg_visibility_score`` (0–1) instead of coverage_rate (%).
  // Kept as a separate component to avoid an over-generalised
  // chart prop API for two callers.
  const dateSet = new Set();
  for (const key of modelKeys) {
    for (const p of data?.[key] || []) dateSet.add(p.date);
  }
  if (dateSet.size === 0) {
    return <ChartEmpty />;
  }
  const dates = Array.from(dateSet).sort();
  const merged = dates.map((date) => {
    const row = { date };
    for (const k of modelKeys) {
      const point = (data[k] || []).find((p) => p.date === date);
      row[k] = point ? point.avg_visibility_score : null;
    }
    return row;
  });
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={merged} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
        <CartesianGrid stroke="#eef2f7" vertical={false} />
        <XAxis
          dataKey="date"
          stroke="#94a3b8"
          fontSize={11}
          tickLine={false}
          axisLine={{ stroke: "#e2e8f0" }}
        />
        <YAxis
          stroke="#94a3b8"
          fontSize={11}
          tickLine={false}
          axisLine={false}
          domain={[0, 1]}
          tickFormatter={(v) => v.toFixed(2)}
        />
        <Tooltip
          formatter={(v, n) => [v === null ? "—" : Number(v).toFixed(2), modelDisplayName(n)]}
          contentStyle={{
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            fontSize: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        />
        <Legend
          formatter={(v) => modelDisplayName(v)}
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontSize: 12 }}
        />
        {modelKeys.map((k) => (
          <Line
            key={k}
            type="monotone"
            dataKey={k}
            stroke={modelColor(k)}
            strokeWidth={2.5}
            dot={{ r: 3, fill: modelColor(k), strokeWidth: 0 }}
            activeDot={{ r: 5 }}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function ShareOfVoice({ data }) {
  const rows = Object.entries(data || {}).map(([name, value]) => ({
    name,
    value: Number(value),
  }));
  if (rows.length === 0 || rows.every((r) => !r.value)) return <ChartEmpty />;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={rows}
          innerRadius={55}
          outerRadius={85}
          paddingAngle={2}
          dataKey="value"
          stroke="none"
        >
          {rows.map((_, i) => (
            <Cell key={i} fill={SOV_PALETTE[i % SOV_PALETTE.length]} />
          ))}
        </Pie>
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
        <Tooltip
          formatter={(v) => `${v}%`}
          contentStyle={{
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            fontSize: 12,
          }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

function SentimentChart({ breakdown, t }) {
  const total =
    (breakdown?.positive || 0) +
    (breakdown?.neutral || 0) +
    (breakdown?.negative || 0) +
    (breakdown?.unknown || 0);
  if (!total) return <ChartEmpty />;
  const items = [
    { key: "positive", color: "#10b981", value: breakdown.positive || 0 },
    { key: "neutral", color: "#f59e0b", value: breakdown.neutral || 0 },
    { key: "negative", color: "#ef4444", value: breakdown.negative || 0 },
    { key: "unknown", color: "#94a3b8", value: breakdown.unknown || 0 },
  ];
  return (
    <div>
      <div className="h-3 w-full rounded-full overflow-hidden flex bg-slate-100">
        {items.map((it) => (
          <div
            key={it.key}
            className="transition-all duration-500"
            style={{
              width: `${(it.value / total) * 100}%`,
              background: it.color,
            }}
          />
        ))}
      </div>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        {items.map((it) => (
          <div key={it.key} className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ background: it.color }}
            />
            <div>
              <div className="text-slate-500 text-xs capitalize">{t(`home.sentiment_${it.key}`)}</div>
              <div className="mono font-semibold text-slate-900">{it.value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PositionDistChart({ dist, t }) {
  const items = [
    { key: "first", label: t("home.positionFirst"), color: "#10b981", value: dist?.first || 0 },
    { key: "second", label: t("home.positionSecond"), color: "#62c4ca", value: dist?.second || 0 },
    { key: "third", label: t("home.positionThird"), color: "#a3dde1", value: dist?.third || 0 },
    { key: "later", label: t("home.positionLater"), color: "#cbd5e1", value: dist?.later || 0 },
    { key: "not_mentioned", label: t("home.positionNot"), color: "#fecaca", value: dist?.not_mentioned || 0 },
  ];
  const total = items.reduce((s, i) => s + i.value, 0);
  if (!total) return <ChartEmpty />;
  return (
    <div className="space-y-3">
      {items.map((it) => {
        const pct = total ? (it.value / total) * 100 : 0;
        return (
          <div key={it.key}>
            <div className="flex justify-between items-baseline text-xs mb-1">
              <span className="text-slate-700 font-medium">{it.label}</span>
              <span className="mono text-slate-500">
                {it.value} <span className="text-slate-400">({pct.toFixed(0)}%)</span>
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, background: it.color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TopPromptsTable({ prompts, t }) {
  if (!prompts.length) return <ChartEmpty />;
  return (
    <div className="overflow-x-auto -mx-6 -mb-6">
      <table className="w-full text-sm">
        <thead className="bg-slate-50/60 text-slate-500 text-left">
          <tr>
            <th className="px-6 py-3 font-medium text-xs uppercase tracking-wider">
              {t("home.topPromptsCol")}
            </th>
            <th className="px-3 py-3 font-medium text-xs uppercase tracking-wider w-32">
              {t("home.topPromptsCoverage")}
            </th>
            <th className="px-3 py-3 font-medium text-xs uppercase tracking-wider w-24">
              {t("home.topPromptsScore")}
            </th>
            <th className="px-3 py-3 font-medium text-xs uppercase tracking-wider">
              {t("home.topPromptsCompetitors")}
            </th>
            <th className="px-6 py-3 font-medium text-xs uppercase tracking-wider w-20 text-right">
              {t("home.topPromptsScans")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {prompts.map((p) => {
            const compEntries = Object.entries(p.competitor_mentions || {});
            return (
            <tr key={p.prompt_id} className="hover:bg-slate-50/60">
              <td className="px-6 py-3">
                <div className="font-medium text-slate-900 truncate max-w-md">
                  {p.prompt_text}
                </div>
                <div className="text-xs text-slate-500 capitalize">
                  {t(`prompts.categories.${p.category}`, p.category)}
                </div>
              </td>
              <td className="px-3 py-3">
                <div className="flex items-center gap-2">
                  <div className="mono text-sm w-12">{p.coverage_rate}%</div>
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-500 rounded-full"
                      style={{ width: `${p.coverage_rate}%` }}
                    />
                  </div>
                </div>
              </td>
              <td className="px-3 py-3 mono text-sm">
                {p.avg_visibility_score.toFixed(2)}
              </td>
              <td className="px-3 py-3">
                {compEntries.length === 0 ? (
                  <span className="text-xs text-slate-400">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {compEntries.map(([label, count]) => (
                      <span
                        key={label}
                        className="text-xs rounded-full px-2 py-0.5 bg-slate-100 text-slate-600"
                      >
                        {label} <span className="mono">×{count}</span>
                      </span>
                    ))}
                  </div>
                )}
              </td>
              <td className="px-6 py-3 mono text-sm text-right text-slate-500">
                {p.scans}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PositionByModel({ distByModel, t, modelKeys }) {
  // Stacked horizontal bar per LLM. Each row sums to 100 % of that model's
  // scans, segmented by where the brand landed (1st / 2nd / 3rd / 4+ / not
  // mentioned). Empty models gracefully show an "empty" rail.
  const buckets = [
    { key: "first", label: t("home.positionFirst"), color: "#10b981" },
    { key: "second", label: t("home.positionSecond"), color: "#62c4ca" },
    { key: "third", label: t("home.positionThird"), color: "#a3dde1" },
    { key: "later", label: t("home.positionLater"), color: "#cbd5e1" },
    { key: "not_mentioned", label: t("home.positionNot"), color: "#fecaca" },
  ];

  const hasAny = modelKeys.some((k) => {
    const d = distByModel?.[k];
    return d && Object.values(d).some((v) => v > 0);
  });
  if (!hasAny) return <ChartEmpty />;

  return (
    <div className="space-y-5">
      {modelKeys.map((k) => {
        const d = distByModel?.[k] || {};
        const total = buckets.reduce((s, b) => s + (d[b.key] || 0), 0);
        return (
          <div key={k}>
            <div className="flex items-center justify-between mb-2">
              <ModelBadge modelKey={k} size="sm" />
              <span className="text-xs text-slate-500 mono">
                {total} {t("home.kpiScans").toLowerCase()}
              </span>
            </div>
            {total === 0 ? (
              <div className="h-2.5 bg-slate-100 rounded-full" />
            ) : (
              <>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden flex">
                  {buckets.map((b) => {
                    const value = d[b.key] || 0;
                    if (!value) return null;
                    return (
                      <div
                        key={b.key}
                        className="transition-all duration-500"
                        style={{
                          width: `${(value / total) * 100}%`,
                          background: b.color,
                        }}
                        title={`${b.label}: ${value} (${((value / total) * 100).toFixed(0)}%)`}
                      />
                    );
                  })}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                  {buckets
                    .filter((b) => (d[b.key] || 0) > 0)
                    .map((b) => (
                      <span key={b.key} className="flex items-center gap-1">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ background: b.color }}
                        />
                        {b.label}{" "}
                        <span className="mono text-slate-400">
                          {Math.round(((d[b.key] || 0) / total) * 100)}%
                        </span>
                      </span>
                    ))}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ScanActivityChart({ data, t }) {
  const rows = data || [];
  if (rows.length === 0) return <ChartEmpty />;
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={rows} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
        <CartesianGrid stroke="#eef2f7" vertical={false} />
        <XAxis
          dataKey="date"
          stroke="#94a3b8"
          fontSize={11}
          tickLine={false}
          axisLine={{ stroke: "#e2e8f0" }}
        />
        <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
        <Tooltip
          formatter={(v) => [v, t("home.scanActivityScans")]}
          contentStyle={{
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            fontSize: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        />
        <Bar dataKey="scans" fill="#2ea7af" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ChartEmpty() {
  const { t } = useTranslation();
  return (
    <div className="h-48 flex items-center justify-center text-sm text-slate-400 italic">
      {t("home.notEnoughData")}
    </div>
  );
}

function LoadingState() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-7 w-48 bg-slate-200 rounded" />
      <div className="grid sm:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 bg-slate-100 rounded-2xl" />
        ))}
      </div>
      <div className="text-slate-500 text-sm">{t("home.loadingDashboard")}</div>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl bg-white border border-slate-200/70 shadow-sm text-center py-16 px-6">
      <h2 className="text-xl font-semibold text-slate-900">{t("home.emptyTitle")}</h2>
      <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">{t("home.emptyBody")}</p>
      <Link to="/dashboard/websites" className="btn-primary mt-6 inline-flex">
        {t("home.emptyButton")}
      </Link>
    </div>
  );
}
